import moment from "moment"

/**
 * DateTime format for different interfaces
 */
const FMTS = {
  DATE: "DD-MM-YYYY",
  DATETIME: "DD-MM-YYYY HH:mm:ss",
  FILE: "YYYY-MM-DDTHHmmss",
  ISODATETIME: "YYYY-MM-DDTHH:mm:ss",
  ISODATE: "YYYY-MM-DD",
  TIME: "HH:mm",
  TIME_S: "HH:mm:ss"
}

/**
 * Time intervals in ms.
 */
const MS_DURATIONS = {
  // nb of ms in 3 day interval
  DAY_3: 259200000
}

/**
 * Get duration between two dates objects
 * @param {*} valueA The first datetime object
 * @param {*} valueB The second datetime object
 * @returns The duration (string)
 */
function getDuration(valueA, valueB) {
  return moment.duration(valueA.diff(valueB)).humanize()
}


function ensureMoment(value, isLocal = true) {
  if (!value) return moment()
  if (moment.isMoment(value)) return value
  if (value instanceof Date) return moment(value)
  if (typeof value === "number") return moment(value) // timestamp ms
  if (typeof value === "string") {
    // si la string inclut un fuseau (Z ou ±HH:mm), conserve-le
    const hasTZ = /([Zz]|[+-]\d{2}:\d{2})$/.test(value)
    return hasTZ ? moment.parseZone(value) : moment(value)
  }
  return moment(value)
}

// Remplace ton dateToString actuel par ceci
function dateToString(value = null, format = FMTS.ISODATETIME, isLocal = true) {
  let m = ensureMoment(value, isLocal)
  if (!m.isValid()) return ""
  m = isLocal ? m.local() : m.utc()
  return m.format(format)
}

/**
 * Get lower value from valueA and valueB
 * @param {*} valueA The first datetime object
 * @param {*} valueB The second datetime object
 * @returns The lowest datetime object
 */
function getMinDatetime(valueA, valueB) {
  if (!valueA) return valueB
  else if (!valueB) return valueA
  else return valueA.isBefore(valueB) ? valueA : valueB
}

/**
 * Get current timezone name
 * @returns The current timezone
 */
function getTimeZoneName() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Check if valueA is before valueB
 * @param {*} valueA The first datetime object
 * @param {*} valueB The second datetime object default to now()
 * @returns Boolean
 */
function isDateBefore(valueA, valueB = null) {
  valueB = valueB || now()
  return valueA.isBefore(valueB)
}

/**
 * Check if value is between min / max if provided
 * @returns Boolean true if date is between else false
 */
function isDateBetween(value, min = null, max = null) {
  return !(min?.isAfter(value) || max?.isBefore(value))
}

/**
 * Check if data is a datetime object
 * @param {*} data The object to check
 * @returns boolean
 */
function isObjectDate(data) {
  return moment.isMoment(data)
}

/**
 * Check if valueA is same day as valueB
 * @param {*} valueA The first datetime object
 * @param {*} valueB The second datetime object default to now()
 * @returns Boolean true if date is between else false
 */
function isSameDay(valueA, valueB = null) {
  valueB = valueB || now()
  return valueA.isSame(valueB, "day")
}

/**
 * Check if data is a datetime string
 * @param {*} data The string to check
 * @returns boolean
 */
function isISODate(data) {
  const regex = /^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(\.\d+)?([+-][0-2]\d:[0-5]\d|Z)?$/g
  return regex.test(data)
}

/**
 * Create object copy of value
 * @returns The new datetime object
 */
function copy(value) {
  return moment(value)
}

/**
 * Get today datetime object. Begin this day at 00:00:00
 * @returns The datetime object
 */
function today() {
  return startOf(now(), "day")
}

/**
 * Get tomorrow datetime object. Begin this day at 00:00:00
 * @returns The datetime object
 */
function tomorrow(data = null) {
  return add(data ? data : today(), 1, "day")
}


/**
 * Get tomorrow datetime object. Begin this day at 00:00:00
 * @returns The datetime object
 */
function next_month(data = null) {
  return add(data ? data : today(), 30, "day")
}

/**
 * Get current datetime object.
 * @returns The datetime object
 */
function now() {
  return moment()
}

/**
 * Update datetime value to add specified amount of time
 * @param {*} value The datetime to update
 * @param {number} amount The amout of time to add
 * @param {string} unit The unit to start from
 * @returns The datetime copy
 */
function add(value, amount, unit) {
  return value.add(amount, unit)
}

/**
 * Update datetime value to make it start at specified unit
 * @param {*} value The datetime to update
 * @param {string} unit The unit to start from
 * @returns The datetime copy
 */
function startOf(value, unit = "day") {
  return value.startOf(unit)
}

/**
 * Format value to dict date to be used by Timeline-JS.
 *  - timeline-js use [1-12] indexed month values
 * @param {*} value The datetime object
 * @returns The date dict object
 */
function toObject(value) {
  return {
    year: value.year(),
    month: value.month() + 1,
    day: value.date(),
    hour: value.hour(),
    minute: value.minute(),
    second: value.second()
  }
}

/**
 * Format value to string.
 * @param {*} value The datetime object, default to now()
 * @returns The date string to date format (DD-MM-YYYY)
 */
function toDate(value = null) {
  return dateToString(value, FMTS.DATE)
}

/**
 * Format value to string.
 * @param {*} value The datetime object, default to now()
 * @param {boolean} isLocal If `true` convert value to local datetime. Either to utc datetime.
 * @returns The datetime string to format (DD-MM-YYYY HH:mm:ss)
 */
function toDatetime(value = null, isLocal = true) {
  return dateToString(value, FMTS.DATETIME, isLocal)
}

/**
 * Format value to string.
 * @param {*} value The datetime object, default to now()
 * @returns The date string to ISO Date format
 */
function toISODate(value = null) {
  return dateToString(value, FMTS.ISODATE)
}

/**
 * Format value to string.
 * @param {*} value The datetime object, default to now()
 * @param {boolean} isLocal If `true` convert value to local datetime. Either to utc datetime.
 * @returns The date string to ISO Date format with time
 */
function toISODatetime(value = null, isLocal = true) {
  return dateToString(value, FMTS.ISODATETIME, isLocal)
}

/**
 * Format value to ISO formated string using UTC timezone.
 * @param {*} value The datetime object
 * @returns The date string
 */
function toISODatetimeUTC(value) {
  return `${dateToString(value, FMTS.ISODATETIME, false)}Z`
}

/**
 * Format value to string.
 * @param {*} value The datetime object, default to now()
 * @returns The time string
 */
function toTime(value = null) {
  return dateToString(value, FMTS.TIME)
}

/**
 * Format datetime. Display date only date is not today
 * @param {*} value The datetime object
 * @returns The datetime string
 */
function toTimeOrDatetime(value) {
  const format = isSameDay(value, now()) ? FMTS.TIME_S : FMTS.DATETIME
  return dateToString(value, format)
}

/**
 * Format value to unix Timestamp (ms).
 * @param {*} value The datetime object, default to now()
 * @returns Int The timestamp
 */
function toTimestamp(value = null) {
  value = value || now()
  return value.valueOf()
}

/**
 * Transform datetime object to string using specified format.
 * @param {*} value The datetime object to convert, default to now()
 * @param {string} format The datetime format to use
 * @param {boolean} isLocal If `true` convert value to local datetime. Either to utc datetime.
 * @returns The datetime string using specified format
 */
/*function dateToString(value = null, format = FMTS.ISODATETIME, isLocal = true) {
  value = value || now()
  value = isLocal ? value.local() : value.utc()
  return value.format(format)
}*/

/**
 * Transform datetime string to datetime object. Must include TZ or local is used.
 * @param {*} data The datetime string to convert
 * @param {boolean} isLocal If `true` create a local datetime either create utc datetime.
 * @returns The datetime object
 */
function stringToDate(data, isLocal = true) {
  return isLocal ? moment(data) : moment.utc(data)
}

export {
  FMTS,
  MS_DURATIONS,
  add,
  copy,
  getDuration,
  getMinDatetime,
  getTimeZoneName,
  isDateBefore,
  isDateBetween,
  isObjectDate,
  isSameDay,
  isISODate,
  now,
  today,
  tomorrow,
  dateToString,
  stringToDate,
  startOf,
  toObject,
  toDate,
  toDatetime,
  toISODate,
  toISODatetime,
  toISODatetimeUTC,
  toTime,
  toTimeOrDatetime,
  toTimestamp,
  next_month
}
